import React, { useMemo } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import EntityHistoryAction from "@mapmycustomers/shared/enum/EntityHistoryAction";
import Feature from "@mapmycustomers/shared/enum/Feature";
import HistoryFieldName from "@mapmycustomers/shared/enum/HistoryFieldName";
import Located from "@mapmycustomers/shared/types/base/Located";
import CustomFieldType from "@mapmycustomers/shared/types/customField/CustomField";
import { CustomFieldValueType } from "@mapmycustomers/shared/types/customField/CustomFieldValueType";
import type { Territory } from "@mapmycustomers/shared/types/entity";
import {
  Company,
  EntitiesSupportingHistory,
  EntityType,
  EntityTypeSupportingHistory,
  Person,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { EntityHistoryRowWithRecord } from "@mapmycustomers/shared/types/entity/EntityHistoryRow";
import { UserRef } from "@mapmycustomers/shared/types/User";
import { AvatarWithName } from "@mapmycustomers/ui";

import configService from "@app/config/ConfigService";
import Path from "@app/enum/Path";
import { getActivityTypes } from "@app/store/activity";
import authService from "@app/store/auth/AuthService";
import { getCustomFieldsForEntity } from "@app/store/customFields";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { getDealLossReasons } from "@app/store/dealLossReasons/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import { getLegacyAppUrl } from "@app/util/appUrl";
import FieldModel from "@app/util/fieldModel/impl/FieldModel";
import useFeatures from "@app/util/hook/useFeatures";

import AddressLine from "../../activity/ActivityAnnotation/TopSection/AddressLine";
import ButtonLink from "../../ButtonLink";
import EntityBadge from "../../EntityBadge";

import styles from "./ValueColumn.module.scss";

const messages = defineMessages({
  addedTo: {
    id: "@component.entityHistory.table.value.addToEntity",
    defaultMessage: "Added to {entities}",
    description: "History modal - table - add to entity cell",
  },
  na: {
    id: "@component.entityHistory.table.value.NA",
    defaultMessage: "N/A",
    description: "History modal - table - N/A cell",
  },
  noAccess: {
    id: "@component.entityHistory.table.value.noAccess",
    defaultMessage: "No access",
    description: "History modal - table - No access cell",
  },
  removedFrom: {
    id: "@component.entityHistory.table.value.removeFromEntity",
    defaultMessage: "Removed from {entities}",
    description: "History modal - table - remove to entity cell",
  },
});

const DOUBLE_DASH = "--";

interface ValueColumnProps {
  entity: EntitiesSupportingHistory;
  entityType: EntityTypeSupportingHistory;
  fieldModel: FieldModel;
  hideAdditionsAndRemovals?: boolean;
  historyRow: EntityHistoryRowWithRecord;
  showEntityView: typeof showEntityView;
  value: unknown;
}

interface Props extends ValueColumnProps {
  activityTypes: ActivityType[];
  customFields: CustomFieldType[];
  funnels: Funnel[];
  lossReasons: DealLossReason[];
  stages: Stage[];
}

const ValueColumn: React.FC<Props> = ({
  activityTypes,
  customFields,
  entity,
  entityType,
  fieldModel,
  funnels,
  hideAdditionsAndRemovals,
  historyRow,
  lossReasons,
  showEntityView,
  stages,
  value,
}) => {
  const intl = useIntl();
  const { hasFeature } = useFeatures();

  const [formattedContent, formattedTooltip]: [React.ReactNode, string | undefined] =
    useMemo(() => {
      let content: React.ReactNode = DOUBLE_DASH,
        tooltip: string | undefined;
      if (
        [
          EntityHistoryAction.ADDED_TO_GROUP,
          EntityHistoryAction.ADDED_TO_ROUTE,
          EntityHistoryAction.ADDED_TO_TERRITORIES,
          EntityHistoryAction.REMOVED_FROM_GROUP,
          EntityHistoryAction.REMOVED_FROM_ROUTE,
          EntityHistoryAction.REMOVED_FROM_TERRITORIES,
        ].includes(historyRow.actionType)
      ) {
        content = intl.formatMessage(messages.na);
      }
      if (
        historyRow.fieldName &&
        [
          HistoryFieldName.ACCOUNT_ID,
          HistoryFieldName.PARENT_ACCOUNT_ID,
          HistoryFieldName.PRIMARY_ACCOUNT_ID,
        ].includes(historyRow.fieldName as HistoryFieldName) &&
        value
      ) {
        content = (
          <ButtonLink
            onClick={() =>
              showEntityView({
                entityId: (value as EntitiesSupportingHistory).id,
                entityType: EntityType.COMPANY,
              })
            }
          >
            {(value as Company).name}
          </ButtonLink>
        );
        tooltip = (value as Company).name;
      } else if (
        historyRow.fieldName &&
        [HistoryFieldName.CONTACT_ID, HistoryFieldName.PRIMARY_CONTACT_ID].includes(
          historyRow.fieldName as HistoryFieldName
        ) &&
        value
      ) {
        content = (
          <ButtonLink
            onClick={() =>
              showEntityView({
                entityId: (value as EntitiesSupportingHistory).id,
                entityType: EntityType.PERSON,
              })
            }
          >
            {(value as Person).name}
          </ButtonLink>
        );
        tooltip = (value as Person).name;
      } else if (
        HistoryFieldName.ACCOUNTS_ID === historyRow.fieldName &&
        value &&
        Array.isArray(value)
      ) {
        content = (
          <>
            {(value as Company[]).map((company, index) => (
              <>
                <ButtonLink
                  onClick={() =>
                    showEntityView({
                      entityId: company.id,
                      entityType: EntityType.COMPANY,
                    })
                  }
                >
                  {company.name}
                </ButtonLink>
                {index < (value as Company[]).length - 1 ? ", " : ""}
              </>
            ))}
          </>
        );
        tooltip = (value as Company[])?.map((company) => company.name).join(", ");
      } else if (historyRow?.fieldName === HistoryFieldName.FUNNEL_ID) {
        const funnel = funnels.find(({ id }) => id === value);
        if (funnel) {
          content = funnel.name;
          tooltip = content.toString();
        }
      } else if (historyRow?.fieldName === HistoryFieldName.DEAL_STAGE_ID) {
        const stage = stages.find(({ id }) => id === value);
        if (stage) {
          content = <EntityBadge entity={stage} />;
          tooltip = stage.name;
        }
      } else if (historyRow?.fieldName === HistoryFieldName.DEAL_LOSS_REASON_ID) {
        const lossReason = lossReasons.find(({ id }) => id === value);
        if (lossReason) {
          content = lossReason.name;
          tooltip = content.toString();
        }
      } else if (
        historyRow?.fieldName &&
        [HistoryFieldName.FREQUENCY_INTERVAL, HistoryFieldName.NOTE].includes(
          historyRow?.fieldName as HistoryFieldName
        )
      ) {
        if (value && value !== null) {
          content = value;
          tooltip = value.toString();
        } else {
          content = DOUBLE_DASH;
        }
      } else if (historyRow?.fieldName === HistoryFieldName.FREQUENCY_ACCEPTED_TYPES) {
        if (value && value !== null) {
          content =
            (value as ActivityType[]).length > 0
              ? (value as ActivityType[]).map(({ name }) => name).join(", ")
              : activityTypes.map(({ name }) => name).join(", ");
          tooltip = content.toString();
        } else {
          content = DOUBLE_DASH;
        }
      } else if (
        [EntityHistoryAction.FILE_ADDED, EntityHistoryAction.FILE_REMOVED].includes(
          historyRow.actionType
        ) &&
        historyRow.file?.name &&
        !hideAdditionsAndRemovals
      ) {
        content = historyRow.file.name;
      } else if (
        [
          EntityHistoryAction.CUSTOM_FIELD_VALUE_ADDED,
          EntityHistoryAction.CUSTOM_FIELD_VALUE_DELETED,
          EntityHistoryAction.CUSTOM_FIELD_VALUE_UPDATED,
          EntityHistoryAction.RECORD_UPDATED,
        ].includes(historyRow.actionType)
      ) {
        let field;
        let formattedEntity;
        if (EntityHistoryAction.RECORD_UPDATED === historyRow.actionType && historyRow.fieldName) {
          formattedEntity = { ...entity, [historyRow.fieldName]: value };
          field = fieldModel.getByName(historyRow.fieldName);
        } else if (
          [
            EntityHistoryAction.CUSTOM_FIELD_VALUE_ADDED,
            EntityHistoryAction.CUSTOM_FIELD_VALUE_DELETED,
            EntityHistoryAction.CUSTOM_FIELD_VALUE_UPDATED,
          ].includes(historyRow.actionType)
        ) {
          if (historyRow.customField?.id) {
            field = fieldModel.getByName(historyRow?.customField?.id.toString() ?? "");
            let customFieldsToUpdate = [...(entity.customFields ?? [])];
            const customFieldIndex = customFieldsToUpdate.findIndex(
              ({ customField }) => customField.id === historyRow?.customField?.id
            );
            if (customFieldIndex !== -1) {
              customFieldsToUpdate = [
                ...customFieldsToUpdate.slice(0, customFieldIndex),
                {
                  ...customFieldsToUpdate[customFieldIndex],
                  value: value as CustomFieldValueType,
                },
                ...customFieldsToUpdate.slice(customFieldIndex + 1),
              ];
            } else {
              const customField = customFields.find(({ id }) => id === historyRow?.customField?.id);
              if (customField) {
                customFieldsToUpdate = [
                  ...customFieldsToUpdate,
                  {
                    id: 0,
                    crmPropertyKey: customField.crmPropertyKey,
                    currencyId: customField.currencyId,
                    customField: { id: customField.id },
                    customFieldId: entity.id,
                    dataType: customField.dataType,
                    esKey: customField.esKey,
                    options: customField.options,
                    value: value as CustomFieldValueType,
                  },
                ];
              }
            }
            formattedEntity = {
              ...entity,
              customFields: customFieldsToUpdate,
            };
          }
        }
        if (field && formattedEntity) {
          content = field.getFormattedValueFor(formattedEntity);
          if (content.toString().trim().length) {
            tooltip = content.toString();
          } else {
            content = DOUBLE_DASH;
          }
        }
      } else if (historyRow.actionType === EntityHistoryAction.OWNERSHIP_CHANGED) {
        content = <AvatarWithName user={value as UserRef} />;
      } else if (historyRow.actionType === EntityHistoryAction.LOCATION_CHANGED) {
        const locatedEntity: Located = { ...entity, ...(value as Located) };
        content = <AddressLine addressRecord={locatedEntity as Company | Person} />;
      }
      const entities = [];
      let entityName = "";

      let message;
      if (
        [
          EntityHistoryAction.ADDED_TO_GROUP,
          EntityHistoryAction.ADDED_TO_ROUTE,
          EntityHistoryAction.ADDED_TO_TERRITORIES,
        ].includes(historyRow.actionType)
      ) {
        message = messages.addedTo;
      } else if (
        [
          EntityHistoryAction.REMOVED_FROM_GROUP,
          EntityHistoryAction.REMOVED_FROM_ROUTE,
          EntityHistoryAction.REMOVED_FROM_TERRITORIES,
        ].includes(historyRow.actionType)
      ) {
        message = messages.removedFrom;
      }
      const baseOldAppUrl = configService.getBaseOldAppUrl();
      const token = authService.getToken();
      if (historyRow.group) {
        let link;
        entityName = historyRow.group.name;
        if (
          [EntityHistoryAction.ADDED_TO_GROUP, EntityHistoryAction.REMOVED_FROM_GROUP].includes(
            historyRow.actionType
          )
        ) {
          if (hasFeature(Feature.MAP)) {
            link = `${window.location.origin}${Path.MAP}/groups/${entityType}/${historyRow.group?.id}`;
          } else {
            link = getLegacyAppUrl(
              baseOldAppUrl,
              token,
              `/${entityType}/groups/list/${historyRow.group?.id}`
            );
          }
        } else if (
          [EntityHistoryAction.ADDED_TO_ROUTE, EntityHistoryAction.REMOVED_FROM_ROUTE].includes(
            historyRow.actionType
          )
        ) {
          link = getLegacyAppUrl(
            baseOldAppUrl,
            token,
            `/${entityType}/routing/edit/${historyRow.group?.id}`
          );
        }
        if (link) {
          entities.push(
            <ButtonLink href={link} target="_blank">
              {historyRow.group?.name ?? ""}
            </ButtonLink>
          );
        }
      } else if (historyRow.territories) {
        const excludedArray = new Set(
          (historyRow.actionType === EntityHistoryAction.ADDED_TO_TERRITORIES
            ? historyRow.old
            : historyRow.new) as Territory["id"][]
        ) as Set<Territory["id"]>;
        historyRow.territories
          .filter(({ id }) => !excludedArray.has(id))
          .forEach(({ id, name }) => {
            entities.push(
              <>
                <ButtonLink
                  href={`${window.location.origin}${Path.MAP}/territories/${id}`}
                  target="_blank"
                >
                  {name}
                </ButtonLink>
                {entities.length ? ", " : ""}
              </>
            );
            entityName = `${entityName}${entityName ? ", " : ""}${name}`;
          });
      }

      if (!hideAdditionsAndRemovals && message && entities.length) {
        content = intl.formatMessage(message, {
          entities,
        });
        tooltip = entityName;
      }
      return [content, tooltip];
    }, [
      activityTypes,
      customFields,
      entity,
      entityType,
      fieldModel,
      funnels,
      hasFeature,
      hideAdditionsAndRemovals,
      historyRow.actionType,
      historyRow.customField?.id,
      historyRow.fieldName,
      historyRow.group,
      historyRow.new,
      historyRow.old,
      historyRow.file,
      historyRow.territories,
      intl,
      lossReasons,
      showEntityView,
      stages,
      value,
    ]);

  return (
    <Tooltip title={formattedTooltip}>
      <div className={styles.container}>{formattedContent}</div>
    </Tooltip>
  );
};

const mapStateToProps = (
  state: RootState,
  ownProps: { entityType: EntityTypeSupportingHistory }
) => ({
  activityTypes: getActivityTypes(state),
  customFields: getCustomFieldsForEntity(state)(ownProps.entityType),
  funnels: getFunnels(state),
  lossReasons: getDealLossReasons(state),
  stages: Object.values(getFunnelStages(state)).flat(),
});

export default connect(mapStateToProps)(ValueColumn);
